import { type ReactNode, useMemo, useState } from 'react';
import { FaHospital } from 'react-icons/fa';
import {
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiLayers,
  FiMapPin,
  FiNavigation2,
  FiSearch,
  FiUser,
  FiX,
} from 'react-icons/fi';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
} from 'tcm-chakra-ui';
import { providerSelectKey, type ProviderOption } from '../../features/tcm-workflow/providerOption';

type ProviderChoiceListProps = {
  providers: ProviderOption[];
  selectedProviderId: string;
  onChange: (id: string) => void;
  /** When true, show a compact summary of slot availability on each card. */
  showSlotsSummary?: boolean;
  /** Shorter closed-state label (name + department); full detail stays in the popover list. */
  compactTrigger?: boolean;
};


function providerRowKey(
  provider: ProviderOption,
  index: number,
): string {
  return `${provider.id}-${provider.practiceId ?? 'np'}-${index}`;
}

function formatDistanceFromPatientMiles(
  miles: number | null | undefined,
): string | null {
  if (miles == null || Number.isNaN(Number(miles))) return null;
  const n = Number(miles);
  const rounded = n >= 100 ? n.toFixed(0) : n.toFixed(1);
  return `${rounded} mi from patient`;
}

const iconMuted = 'text.brandLight';
const PROVIDER_BADGE_MIN_H = '1.75rem';
const providerBadgeBaseProps = {
  px: 2.5,
  py: 1,
  minH: PROVIDER_BADGE_MIN_H,
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 'full',
  borderWidth: '1px',
  fontSize: 'xs',
  fontWeight: '600',
};

/** Closed trigger uses smaller text than list cards; keep icons at 12px so they match list density. */
const TRIGGER_ICON_PX = 12;

function EmploymentStatusBadge({
  isEmployed,
}: {
  isEmployed: boolean;
}) {
  return (
    <HStack
      gap={1.5}
      px={providerBadgeBaseProps.px}
      py={providerBadgeBaseProps.py}
      minH={providerBadgeBaseProps.minH}
      borderRadius="full"
      borderWidth="1px"
      borderColor={isEmployed ? 'green.200' : 'orange.200'}
      bg={isEmployed ? 'green.50' : 'orange.50'}
      align="center"
    >
      <Box
        color={isEmployed ? 'green.700' : 'orange.800'}
        flexShrink={0}
        aria-hidden
      >
        {isEmployed ? (
          <FiCheck size={11} strokeWidth={2} />
        ) : (
          <FiX size={11} strokeWidth={2} />
        )}
      </Box>
      <Text
        fontSize="xs"
        fontWeight="600"
        color={isEmployed ? 'green.700' : 'orange.800'}
      >
        {isEmployed ? 'Employed' : 'External'}
      </Text>
    </HStack>
  );
}

function IconText({
  icon,
  children,
  fontSize = 'xs',
  fontWeight,
}: {
  icon: ReactNode;
  children: ReactNode;
  fontSize?: string;
  fontWeight?: string;
}) {
  return (
    <HStack gap={1.5} align="flex-start">
      <Box
        color={iconMuted}
        flexShrink={0}
        lineHeight={1.2}
        pt="0.1em"
      >
        {icon}
      </Box>
      <Text
        color="text.secondary"
        fontSize={fontSize}
        fontWeight={fontWeight}
        lineHeight="short"
      >
        {children}
      </Text>
    </HStack>
  );
}

function MultiClinicBadge({
  practiceNames,
}: {
  practiceNames: string[];
}) {
  return (
    <Tooltip.Root openDelay={150} closeDelay={100}>
      <Tooltip.Trigger asChild>
        <Badge
          display="inline-flex"
          alignItems="center"
          gap="0.25rem"
          px="0.45rem"
          py="0.2rem"
          borderRadius="full"
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          color="blue.700"
          fontSize="0.65rem"
          fontWeight="700"
          cursor="default"
          flexShrink={0}
          letterSpacing="0.01em"
          aria-label={`Associated with ${practiceNames.length} clinics`}
        >
          <Box as="span" display="inline-flex" alignItems="center" gap="0.2rem">
            <FaHospital size={10} />
            <span>{practiceNames.length} Clinics</span>
          </Box>
        </Badge>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content
          bg="background.brandDark"
          color="white"
          borderRadius="md"
          px={3}
          py={2}
          maxW="14rem"
          fontSize="xs"
        >
          <Stack gap={1}>
            <Text fontSize="xs" fontWeight="700" color="white">
              {practiceNames.length} clinics:
            </Text>
            {practiceNames.map((name) => (
              <Text key={name} fontSize="xs" color="white" opacity={0.85}>
                {name}
              </Text>
            ))}
          </Stack>
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
}

function ProviderRichCardBody({
  provider,
  showSlotsSummary,
  linkedPracticeNames,
}: {
  provider: ProviderOption;
  showSlotsSummary: boolean;
  linkedPracticeNames?: string[];
}) {
  const hasSlotsInWindow =
    (provider.availableSlotsInWindow ?? []).length > 0;

  const firstSlotStartLabel = (() => {
    if (!showSlotsSummary || !provider.isEmployed) return null;
    const slots = provider.availableSlotsInWindow;
    if (!slots?.length) return null;
    const timestamps = slots
      .map((s) => Date.parse(s.start_time))
      .filter((ms) => !Number.isNaN(ms));
    if (!timestamps.length) return null;
    const d = new Date(Math.min(...timestamps));
    return `Starting ${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`;
  })();
  const distanceLabel = formatDistanceFromPatientMiles(
    provider.distanceFromPatientMiles,
  );

  return (
    <Stack gap={3.5}>
      <HStack
        justify="space-between"
        align="flex-start"
        gap={{ base: 3, md: 4 }}
        wrap="wrap"
      >
        <Stack gap={2}>
          <HStack gap={2} align="center">
            <Box color={iconMuted} flexShrink={0} aria-hidden>
              <FiUser size={16} />
            </Box>
            <Text
              fontWeight="700"
              color="text.brandDark"
              fontSize="md"
            >
              {provider.name}
            </Text>
            {linkedPracticeNames ? (
              <MultiClinicBadge practiceNames={linkedPracticeNames} />
            ) : null}
          </HStack>
          {provider.nickname ? (
            <Text pl={{ base: 0, sm: 6 }} fontSize="sm" color="text.secondary">
              {provider.nickname}
            </Text>
          ) : null}
          <Stack gap={1.5} pl={{ base: 0, sm: 6 }}>
            <IconText icon={<FiBriefcase size={13} aria-hidden />}>
              {provider.specialty}
            </IconText>
            <IconText icon={<FiLayers size={13} aria-hidden />}>
              Department: {provider.department}
            </IconText>
            {distanceLabel ? (
              <IconText
                icon={<FiNavigation2 size={13} aria-hidden />}
                fontWeight="600"
              >
                {distanceLabel}
              </IconText>
            ) : null}
          </Stack>
        </Stack>
        <HStack
          gap={2}
          rowGap={2}
          wrap="wrap"
          justify="flex-end"
          align="flex-start"
        >
          <EmploymentStatusBadge isEmployed={provider.isEmployed} />
          {provider.isPatientPcp ? (
            <Badge
              {...providerBadgeBaseProps}
              borderColor="purple.200"
              bg="purple.50"
              color="purple.800"
            >
              Established PCP
            </Badge>
          ) : null}
          {provider.isEmployed ? (
            <Badge
              {...providerBadgeBaseProps}
              borderColor={
                provider.isAcceptingNewPatients
                  ? 'green.200'
                  : 'orange.200'
              }
              bg={
                provider.isAcceptingNewPatients
                  ? 'green.50'
                  : 'orange.50'
              }
              color={
                provider.isAcceptingNewPatients
                  ? 'green.800'
                  : 'orange.900'
              }
            >
              {provider.isAcceptingNewPatients
                ? 'Accepting new patients'
                : 'Not accepting new patients'}
            </Badge>
          ) : null}
        </HStack>
      </HStack>

      {provider.practiceRecord ? (
        <HStack
          align="flex-start"
          gap={3}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="background.primary"
          px={3}
          py={2.5}
        >
          <Box color={iconMuted} flexShrink={0} mt={0.5} aria-hidden>
            <FiMapPin size={15} />
          </Box>
          <Stack gap={0.5} minW={0}>
            <Text
              color="text.brandDark"
              fontWeight="600"
              fontSize="xs"
            >
              {provider.practiceRecord.name}
            </Text>
            <Text color="text.secondary" fontSize="xs">
              {provider.practiceRecord.location}
            </Text>
            {provider.practiceRecord.address ? (
              <Text color="text.secondary" fontSize="xs">
                {provider.practiceRecord.address}
              </Text>
            ) : null}
          </Stack>
        </HStack>
      ) : null}

      {showSlotsSummary && provider.isEmployed ? (
        <HStack gap={2.5} wrap="wrap" align="center" pt={0.5}>
          <Box
            color={hasSlotsInWindow ? 'green.600' : 'red.500'}
            flexShrink={0}
            aria-hidden
          >
            <FiCalendar size={15} />
          </Box>
          <Badge
            px={2.5}
            py={1}
            borderRadius="full"
            bg={hasSlotsInWindow ? 'green.50' : 'red.50'}
            color={hasSlotsInWindow ? 'green.800' : 'red.800'}
            fontSize="xs"
          >
            {hasSlotsInWindow ? 'Slots available' : 'No slots'}
          </Badge>
          {hasSlotsInWindow ? (
            <HStack gap={1} align="center">
              <Text fontSize="xs" color="green.400" aria-hidden>·</Text>
              <Text fontSize="xs" color="green.700" fontWeight="medium">
                {firstSlotStartLabel ?? 'In follow-up window'}
              </Text>
            </HStack>
          ) : (
            <Text fontSize="xs" color="text.secondary">
              No availability in follow-up window
            </Text>
          )}
        </HStack>
      ) : null}
    </Stack>
  );
}

export function ProviderChoiceList({
  providers,
  selectedProviderId,
  onChange,
  showSlotsSummary = true,
  compactTrigger = false,
}: ProviderChoiceListProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selectedIndex = useMemo(
    () => providers.findIndex((p) => providerSelectKey(p) === selectedProviderId),
    [providers, selectedProviderId],
  );
  const selectedProvider =
    selectedIndex >= 0 ? providers[selectedIndex] : undefined;
  const selectedDistanceLabel = formatDistanceFromPatientMiles(
    selectedProvider?.distanceFromPatientMiles,
  );

  const filteredProviders = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return providers;

    return providers.filter((p) => {
      const hay = [p.name, p.nickname, p.practiceRecord?.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [providers, filter]);

  // Map provider id -> practice names, only for providers that appear at multiple clinics.
  const multiClinicPracticeNames = useMemo(() => {
    const grouped = new Map<string, string[]>();
    for (const p of providers) {
      const practiceName = p.practiceRecord?.name;
      if (!practiceName) continue;
      const existing = grouped.get(p.id) ?? [];
      if (!existing.includes(practiceName)) {
        grouped.set(p.id, [...existing, practiceName]);
      }
    }
    const result = new Map<string, string[]>();
    for (const [id, names] of grouped) {
      if (names.length > 1) result.set(id, names);
    }
    return result;
  }, [providers]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setFilter('');
  };

  return (
    <PopoverRoot
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
        if (!e.open) {
          setFilter('');
        }
      }}
      positioning={{ placement: 'bottom-start', sameWidth: true }}
      lazyMount
      unmountOnExit
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          w="100%"
          justifyContent="space-between"
          h="auto"
          minH="3rem"
          py={3.5}
          px={4}
          textAlign="left"
          borderColor="border.gray"
          bg="background.surfaceTertiary"
          css={{
            borderColor: 'var(--tcm-colors-border-gray)',
            backgroundColor:
              'var(--tcm-colors-background-surfaceTertiary)',
          }}
          _hover={{ borderColor: 'background.brandLight' }}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {selectedProvider ? (
            <Stack
              gap={compactTrigger ? 2 : 3}
              align="stretch"
              flex="1"
              minW={0}
            >
              <HStack gap={2} align="center" minW={0}>
                <Box
                  color={iconMuted}
                  flexShrink={0}
                  display="inline-flex"
                  alignItems="center"
                  aria-hidden
                >
                  <FiUser size={TRIGGER_ICON_PX} />
                </Box>
                <Text
                  fontWeight="700"
                  color="text.brandDark"
                  fontSize="sm"
                  lineClamp={compactTrigger ? 2 : 1}
                >
                  {selectedProvider.name}
                </Text>
                {multiClinicPracticeNames.has(selectedProvider.id) ? (
                  <MultiClinicBadge
                    practiceNames={multiClinicPracticeNames.get(selectedProvider.id)!}
                  />
                ) : null}
              </HStack>
              {selectedProvider.nickname ? (
                <Text
                  pl={{ base: 0, sm: 5 }}
                  fontSize="xs"
                  color="text.secondary"
                >
                  {selectedProvider.nickname}
                </Text>
              ) : null}
              {selectedDistanceLabel ? (
                <HStack
                  gap={1.5}
                  align="center"
                  pl={{ base: 0, sm: 5 }}
                  minW={0}
                >
                  <Box
                    color={iconMuted}
                    flexShrink={0}
                    display="inline-flex"
                    alignItems="center"
                    aria-hidden
                  >
                    <FiNavigation2 size={TRIGGER_ICON_PX} />
                  </Box>
                  <Text
                    color="text.secondary"
                    fontSize="xs"
                    lineClamp={1}
                    fontWeight="600"
                  >
                    {selectedDistanceLabel}
                  </Text>
                </HStack>
              ) : null}
              {compactTrigger ? (
                <HStack
                  gap={1.5}
                  align="flex-start"
                  pl={{ base: 0, sm: 5 }}
                  minW={0}
                >
                  <Box
                    color={iconMuted}
                    flexShrink={0}
                    mt="2px"
                    display="inline-flex"
                    aria-hidden
                  >
                    <FiLayers size={TRIGGER_ICON_PX} />
                  </Box>
                  <Text
                    color="text.secondary"
                    fontSize="xs"
                    lineClamp={1}
                    lineHeight="1.45"
                  >
                    {selectedProvider.department}
                  </Text>
                </HStack>
              ) : (
                <HStack
                  gap={1.5}
                  align="flex-start"
                  pl={{ base: 0, sm: 5 }}
                  minW={0}
                >
                  <Box
                    color={iconMuted}
                    flexShrink={0}
                    mt="2px"
                    display="inline-flex"
                    aria-hidden
                  >
                    <FiMapPin size={TRIGGER_ICON_PX} />
                  </Box>
                  <Text
                    color="text.secondary"
                    fontSize="xs"
                    lineClamp={2}
                    lineHeight="1.45"
                  >
                    {selectedProvider.department}
                    {selectedProvider.practiceRecord?.address
                      ? ` · ${selectedProvider.practiceRecord.address}`
                      : selectedProvider.practiceRecord?.location
                        ? ` · ${selectedProvider.practiceRecord.location}`
                        : ''}
                  </Text>
                </HStack>
              )}
              {!compactTrigger ? (
                <HStack
                  gap={2}
                  rowGap={2}
                  flexWrap="wrap"
                  align="flex-start"
                  pt={0.5}
                >
                  <EmploymentStatusBadge
                    isEmployed={selectedProvider.isEmployed}
                  />
                  {selectedProvider.isPatientPcp ? (
                    <Badge
                      {...providerBadgeBaseProps}
                      borderColor="purple.200"
                      bg="purple.50"
                      color="purple.800"
                    >
                      Established PCP
                    </Badge>
                  ) : null}
                  {selectedProvider.isEmployed ? (
                    <Badge
                      {...providerBadgeBaseProps}
                      borderColor={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.200'
                          : 'orange.200'
                      }
                      bg={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.50'
                          : 'orange.50'
                      }
                      color={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.800'
                          : 'orange.900'
                      }
                    >
                      {selectedProvider.isAcceptingNewPatients
                        ? 'Accepting new patients'
                        : 'Not accepting new patients'}
                    </Badge>
                  ) : null}
                </HStack>
              ) : (
                <HStack
                  gap={2}
                  rowGap={2}
                  flexWrap="wrap"
                  align="flex-start"
                  pt={0.5}
                >
                  <EmploymentStatusBadge
                    isEmployed={selectedProvider.isEmployed}
                  />
                  {selectedProvider.isPatientPcp ? (
                    <Badge
                      {...providerBadgeBaseProps}
                      borderColor="purple.200"
                      bg="purple.50"
                      color="purple.800"
                    >
                      Established PCP
                    </Badge>
                  ) : null}
                  {selectedProvider.isEmployed ? (
                    <Badge
                      {...providerBadgeBaseProps}
                      borderColor={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.200'
                          : 'orange.200'
                      }
                      bg={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.50'
                          : 'orange.50'
                      }
                      color={
                        selectedProvider.isAcceptingNewPatients
                          ? 'green.800'
                          : 'orange.900'
                      }
                    >
                      {selectedProvider.isAcceptingNewPatients
                        ? 'Accepting new patients'
                        : 'Not accepting new patients'}
                    </Badge>
                  ) : null}
                </HStack>
              )}
            </Stack>
          ) : (
            <Text color="text.secondary" fontSize="sm">
              Select a provider…
            </Text>
          )}
          <FiChevronDown
            style={{ flexShrink: 0, marginLeft: 8, opacity: 0.7 }}
            aria-hidden
          />
        </Button>
      </PopoverTrigger>

      <PopoverPositioner>
        <PopoverContent
          w="100%"
          maxW="100%"
          bg="background.primary"
          borderWidth="1px"
          borderColor="border.gray"
          boxShadow="lg"
          css={{
            borderColor: 'var(--tcm-colors-border-gray)',
            backgroundColor: 'var(--tcm-colors-background-primary)',
          }}
        >
          <PopoverBody p={3} bg="background.surfaceTertiary">
            <Stack gap={2}>
              <Box position="relative">
                <Box
                  position="absolute"
                  left={3}
                  top="50%"
                  transform="translateY(-50%)"
                  color={iconMuted}
                  pointerEvents="none"
                  zIndex={1}
                  aria-hidden
                >
                  <FiSearch size={16} />
                </Box>
                <Input
                  size="sm"
                  pl={9}
                  placeholder="Search by name…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  borderColor="border.gray"
                  bg="background.primary"
                  css={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'var(--tcm-colors-border-gray)',
                    backgroundColor:
                      'var(--tcm-colors-background-primary)',
                  }}
                  _focusVisible={{
                    borderColor: 'background.brandLight',
                    boxShadow:
                      '0 0 0 1px var(--tcm-colors-text-brandLight)',
                  }}
                />
              </Box>
              <Box
                maxH="min(40vh, 320px)"
                overflowY="auto"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
                bg="background.surfaceTertiary"
                css={{
                  borderColor: 'var(--tcm-colors-border-gray)',
                  backgroundColor:
                    'var(--tcm-colors-background-surfaceTertiary)',
                }}
                role="listbox"
                aria-label="Providers"
              >
                {filteredProviders.length === 0 ? (
                  <Box px={3} py={4}>
                    <Text fontSize="sm" color="text.secondary">
                      No providers match your search.
                    </Text>
                  </Box>
                ) : (
                  <Stack gap={1.5} p={2}>
                    {filteredProviders.map((provider, index) => {
                      const isActive =
                        providerSelectKey(provider) === selectedProviderId;

                      return (
                        <Box
                          key={providerRowKey(provider, index)}
                          role="option"
                          aria-selected={isActive}
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            w="100%"
                            cursor="pointer"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={
                              isActive
                                ? 'background.brandLight'
                                : 'transparent'
                            }
                            bg={
                              isActive
                                ? 'blue.50'
                                : 'background.surfaceTertiary'
                            }
                            textAlign="left"
                            transition="background 0.15s ease"
                            _hover={{
                              bg: isActive
                                ? 'blue.50'
                                : 'background.surfaceTertiary',
                            }}
                            onClick={() => handleSelect(providerSelectKey(provider))}
                            onKeyDown={(e) => {
                              if (
                                e.key === 'Enter' ||
                                e.key === ' '
                              ) {
                                e.preventDefault();
                                handleSelect(providerSelectKey(provider));
                              }
                            }}
                            p={4}
                          >
                            <ProviderRichCardBody
                              provider={provider}
                              showSlotsSummary={showSlotsSummary}
                              linkedPracticeNames={multiClinicPracticeNames.get(provider.id)}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  );
}
